import React from 'react';
import {
  Building2,
  Calendar,
  FileText,
  User,
  Users,
} from 'lucide-react';
import { formatPersonName } from '../lib/formatPersonName';
import { formatSqm, formatVmkCoverDate } from '../lib/vmkQuoteTable';
import { formatQuoteDisplayReference } from '../lib/quoteNumber';
import { VmkBrandedPageHeader, VmkTemplateFooter } from './VmkBrandedTemplateChrome';
import { VmkQuoteOfferBlock } from './VmkQuotePricingPage';
import { formatTemplatePageLabel } from '../lib/vmkQuoteDocumentLayout';
import { VMK_PROPOSAL_BENEFIT_ICONS, VmkProposalCircleCheckIcon, VmkSpecsCheckIcon } from './VmkProposalBenefitIcons';
import { VMK_SPEC_ILLUSTRATIONS, VmkSpecGlassIllustration } from './VmkSpecIllustrations';
import {
  resolveOrderGlazingTypeId,
  getGlazingSpecLeadKey,
  getGlazingSpecItems,
} from '../lib/vmkGlazing';
import {
  getProfileSpecLead,
  getProfileSpecItems,
  resolveOrderProfileSystemId,
} from '../lib/vmkProductSpec';
import { VMK_INVESTMENT_INCLUDED_ICONS } from './VmkInvestmentIncludedIcons';
import { buildVmkInvestmentBreakdown } from '../lib/vmkInvestmentBreakdown';
import { getQuoteExportDeductionInclVat } from '../lib/pricingAdjustments';
import { VMK_WARRANTY_ICONS } from './VmkWarrantyIcons';
import { VMK_PROCESS_STEP_ICONS } from './VmkProcessStepIcons';
import { VMK_SUSTAINABILITY_ICONS } from './VmkSustainabilityIcons';
import VmkAgreementQrCode from './VmkAgreementQrCode';

const publicUrl = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

const projectAddress = (clientDetails) => {
  const parts = [clientDetails?.address, clientDetails?.postalCode, clientDetails?.city].filter(Boolean);
  return parts.join(', ') || '—';
};

const CoverMetaRow = ({ icon: Icon, label, value }) => (
  <div className="vmk-cover-meta-row">
    <Icon className="vmk-cover-meta-icon" aria-hidden />
    <dt className="vmk-cover-meta-label">{label}</dt>
    <dd className="vmk-cover-meta-value">{value}</dd>
  </div>
);

const VmkCoverPage = ({ pageNumber, quoteNumber, clientDetails, advisorName, t }) => {
  const clientName = formatPersonName(clientDetails?.name) || '—';
  const address = projectAddress(clientDetails);
  const coverDate = formatVmkCoverDate();
  const advisor = advisorName || t('quote.template.defaultAdvisor');

  return (
    <div className="quote-pdf-page vmk-cover-page mb-4" data-pdf-page>
      <div className="vmk-cover-badge">{formatTemplatePageLabel(pageNumber)}</div>
      <div className="vmk-cover-shell">
        <div className="vmk-cover-hero-block">
          <div className="vmk-cover-hero" aria-hidden="true">
            <img
              src={publicUrl('/images/vmk-offerte-sustainability-promo.png')}
              alt=""
              className="vmk-cover-hero-image"
            />
          </div>
        </div>

        <section className="vmk-cover-panel">
          <div className="vmk-cover-panel-fold" aria-hidden="true" />
          <div className="vmk-cover-panel-inner">
            <div className="vmk-cover-panel-brand">
              <img
                src={publicUrl('/images/vmk-kozijnen-logo-white.png')}
                alt="VMK Kozijnen"
                className="vmk-cover-logo vmk-cover-logo--panel"
              />
              <p className="vmk-cover-tagline vmk-cover-tagline--panel">
                {t('quote.template.coverTagline')}
              </p>
            </div>
            <h1 className="vmk-cover-title">{t('quote.template.coverTitle')}</h1>
            <p className="vmk-cover-subtitle">{t('quote.template.coverSubtitle')}</p>
            <div className="vmk-cover-rule" />

            <dl className="vmk-cover-meta">
              <CoverMetaRow icon={Users} label={t('quote.template.metaFor')} value={clientName} />
              <CoverMetaRow icon={Building2} label={t('quote.template.metaProject')} value={address} />
              <CoverMetaRow
                icon={FileText}
                label={t('quote.template.metaQuoteNo')}
                value={formatQuoteDisplayReference(quoteNumber, clientDetails?.name) || '—'}
              />
              <CoverMetaRow icon={Calendar} label={t('quote.template.metaDate')} value={coverDate} />
              <CoverMetaRow icon={User} label={t('quote.template.metaAdvisor')} value={advisor} />
            </dl>
          </div>
        </section>
      </div>
    </div>
  );
};

const VmkProposalPage = ({ pageNumber, clientDetails, t }) => {
  const recipient = formatPersonName(clientDetails?.name) || 'relatie';
  const benefitLabels = [
    t('quote.template.benefitEnergy'),
    t('quote.template.benefitComfort'),
    t('quote.template.benefitValue'),
    t('quote.template.benefitMaintenance'),
    t('quote.template.benefitSound'),
    t('quote.template.benefitSecurity'),
  ];
  const benefits = benefitLabels.map((label, index) => ({
    Icon: VMK_PROPOSAL_BENEFIT_ICONS[index],
    label,
  }));

  return (
    <div className="quote-pdf-page vmk-proposal-page mb-4" data-pdf-page>
      <div className="vmk-proposal-shell">
        <VmkBrandedPageHeader pageNumber={pageNumber} />

        <div className="vmk-proposal-main">
          <h2 className="vmk-proposal-title">{t('quote.template.proposalTitle')}</h2>

          <div className="vmk-proposal-intro">
            <div className="vmk-proposal-intro-text">
              <p>{t('quote.template.proposalGreeting', { name: recipient })}</p>
              <p>{t('quote.template.proposalIntro1')}</p>
              <p>{t('quote.template.proposalIntro2')}</p>
              <p>{t('quote.template.proposalIntro3')}</p>
            </div>
            <div className="vmk-proposal-intro-photo">
              <img src={publicUrl('/images/vmk-offerte-proposal-entrance.png')} alt="" />
            </div>
          </div>

          <section className="vmk-proposal-benefits-section">
            <h3 className="vmk-proposal-benefits-title">{t('quote.template.benefitsTitle')}</h3>
            <div className="vmk-proposal-benefits">
              {benefits.map(({ Icon, label }) => (
                <div key={label} className="vmk-proposal-benefit">
                  <div className="vmk-proposal-benefit-icon-wrap" aria-hidden>
                    <Icon />
                  </div>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="vmk-proposal-why">
            <h3 className="vmk-proposal-why-title">{t('quote.template.whyTitle')}</h3>
            <div className="vmk-proposal-why-columns">
              <ul className="vmk-proposal-why-list">
                {(t('quote.template.whyItemsLeft', { returnObjects: true }) || []).map((item) => (
                  <li key={item}>
                    <VmkProposalCircleCheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <ul className="vmk-proposal-why-list">
                {(t('quote.template.whyItemsRight', { returnObjects: true }) || []).map((item) => (
                  <li key={item}>
                    <VmkProposalCircleCheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <VmkTemplateFooter className="vmk-proposal-footer" />
      </div>
    </div>
  );
};

const VmkSpecsPage = ({ pageNumber, savedQuotes = [], t }) => {
  const glazingTypeId = resolveOrderGlazingTypeId(savedQuotes);
  const profileSystemId = resolveOrderProfileSystemId(savedQuotes);
  const profileLead = getProfileSpecLead(profileSystemId);
  const profileItems = getProfileSpecItems(profileSystemId, t);
  const glazingLead = t(getGlazingSpecLeadKey(glazingTypeId));
  const glazingItems = getGlazingSpecItems(glazingTypeId, t);

  const sections = [
    {
      Illustration: VMK_SPEC_ILLUSTRATIONS[0],
      title: t('quote.template.specProfileTitle'),
      lead: profileLead,
      items: profileItems,
    },
    {
      Illustration: () => <VmkSpecGlassIllustration glazingTypeId={glazingTypeId} />,
      title: t('quote.template.specGlassTitle'),
      lead: glazingLead,
      items: glazingItems,
    },
    {
      Illustration: VMK_SPEC_ILLUSTRATIONS[1],
      title: t('quote.template.specSecurityTitle'),
      lead: t('quote.template.specSecurityLead'),
      items: t('quote.template.specSecurityItems', { returnObjects: true }) || [],
    },
  ];

  return (
    <div className="quote-pdf-page vmk-specs-page mb-4" data-pdf-page>
      <div className="vmk-specs-shell">
        <VmkBrandedPageHeader pageNumber={pageNumber} className="vmk-specs-header">
          <h2 className="vmk-proposal-title">{t('quote.template.specsTitle')}</h2>
        </VmkBrandedPageHeader>

        <div className="vmk-specs-main">
          <div className="vmk-specs-blocks">
            {sections.map(({ Illustration, title, lead, items }) => (
              <article key={title} className="vmk-specs-card">
                <div className="vmk-specs-icon-ring">
                  <Illustration />
                </div>
                <div className="vmk-specs-card-body">
                  <h3 className="vmk-specs-card-label">{title}</h3>
                  <p className="vmk-specs-card-lead">{lead}</p>
                  <ul className="vmk-specs-list">
                    {items.map((item) => (
                      <li key={item}>
                        <VmkSpecsCheckIcon />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const VmkTemplateShell = ({ pageNumber, children, className = '' }) => (
  <div className={`quote-pdf-page mb-4 vmk-template-page ${className}`} data-pdf-page>
    <div className="vmk-proposal-shell">
      <VmkBrandedPageHeader pageNumber={pageNumber} />
      <div className="vmk-template-main">{children}</div>
      <VmkTemplateFooter className="vmk-proposal-footer" />
    </div>
  </div>
);

const VmkInvestmentPage = ({
  pageNumber,
  quoteNumber,
  clientDetails,
  totalPrice,
  totalAreaSqm,
  formatPrice,
  getQuoteLineTotal,
  savedQuotes = [],
  sectionSummaries = null,
  multiSectionMode = false,
  t,
}) => {
  const exportDeductionInclVat = getQuoteExportDeductionInclVat(clientDetails);
  const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

  if (multiSectionMode && sectionSummaries?.length > 0) {
    return (
      <div className="quote-pdf-page vmk-investment-page mb-4" data-pdf-page>
        <div className="vmk-proposal-shell vmk-investment-shell">
          <VmkBrandedPageHeader pageNumber={pageNumber} className="vmk-investment-header" />

          <div className="vmk-investment-main">
            <VmkQuoteOfferBlock
              quoteNumber={quoteNumber}
              clientName={clientDetails?.name}
              showDate={false}
              showIntro={false}
            />

            <div className="vmk-investment-body">
              <div className="vmk-investment-hero">
                <span className="vmk-investment-hero-label">{t('quote.multiSectionInvestmentTitle')}</span>
              </div>

              <p className="text-[10px] text-slate-600 mb-3 leading-snug">
                {t('quote.multiSectionInvestmentNote')}
              </p>

              <div className="vmk-investment-table-wrap">
                <table className="vmk-investment-table">
                  <thead>
                    <tr>
                      <th>{t('summary.sectionName')}</th>
                      <th>{t('quote.template.tableAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionSummaries.map((section) => (
                      <tr key={section.id || section.name}>
                        <td>{section.name}</td>
                        <td>{formatPrice(section.totalInclVat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <section className="vmk-investment-included-section">
                <h3 className="vmk-investment-included-title">{t('quote.template.includedTitle')}</h3>
                <div className="vmk-investment-included-grid">
                  {VMK_INVESTMENT_INCLUDED_ICONS.map((Icon, index) => {
                    const includedLabels = t('quote.template.includedItems', { returnObjects: true }) || [];
                    return (
                      <div key={includedLabels[index] || index} className="vmk-investment-included-item">
                        <div className="vmk-investment-included-icon-wrap" aria-hidden>
                          <Icon />
                        </div>
                        <span>{includedLabels[index]}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          </div>

          <VmkTemplateFooter className="vmk-proposal-footer" />
        </div>
      </div>
    );
  }

  const { totalInclVat } = buildVmkInvestmentBreakdown({
    savedQuotes,
    getQuoteLineTotal,
    totalPriceExVat: totalPrice,
  });
  const subtotalInclVatBeforeDeduction = roundMoney(totalInclVat + exportDeductionInclVat);
  const deductionInclVat = exportDeductionInclVat;
  const rowLabels = [t('quote.template.rowFrames')];
  const displayRowsInclVat = [subtotalInclVatBeforeDeduction];
  if (exportDeductionInclVat > 0) {
    rowLabels.push(t('quote.exportDeductionLine'));
    displayRowsInclVat.push(-deductionInclVat);
  }
  const includedLabels = t('quote.template.includedItems', { returnObjects: true }) || [];

  return (
    <div className="quote-pdf-page vmk-investment-page mb-4" data-pdf-page>
      <div className="vmk-proposal-shell vmk-investment-shell">
        <VmkBrandedPageHeader pageNumber={pageNumber} className="vmk-investment-header" />

        <div className="vmk-investment-main">
          <VmkQuoteOfferBlock
            quoteNumber={quoteNumber}
            clientName={clientDetails?.name}
            showDate={false}
            showIntro={false}
          />

          <div className="vmk-investment-body">
          <div className="vmk-investment-hero">
            <span className="vmk-investment-hero-label">{t('quote.template.totalInvestment')}</span>
            <span className="vmk-investment-hero-amount">{formatPrice(totalInclVat)}</span>
            <span className="vmk-investment-hero-vat">{t('quote.template.inclVat')}</span>
            {totalAreaSqm > 0 && (
              <span className="vmk-investment-hero-area">
                {t('quote.template.totalArea')}: {formatSqm(totalAreaSqm)}
              </span>
            )}
          </div>

          <div className="vmk-investment-table-wrap">
            <table className="vmk-investment-table">
              <thead>
                <tr>
                  <th>{t('quote.template.tableDescription')}</th>
                  <th>{t('quote.template.tableAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {rowLabels.map((label, index) => {
                  const amount = displayRowsInclVat[index];
                  const isDeduction = amount < 0;
                  return (
                    <tr key={label} className={isDeduction ? 'vmk-investment-deduction-row' : undefined}>
                      <td>{label}</td>
                      <td className={isDeduction ? 'vmk-investment-deduction-amount' : undefined}>
                        {isDeduction ? `−${formatPrice(Math.abs(amount))}` : formatPrice(amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t('quote.template.tableTotal')}</td>
                  <td>{formatPrice(totalInclVat)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <section className="vmk-investment-included-section">
            <h3 className="vmk-investment-included-title">{t('quote.template.includedTitle')}</h3>
            <div className="vmk-investment-included-grid">
              {VMK_INVESTMENT_INCLUDED_ICONS.map((Icon, index) => (
                <div key={includedLabels[index] || index} className="vmk-investment-included-item">
                  <div className="vmk-investment-included-icon-wrap" aria-hidden>
                    <Icon />
                  </div>
                  <span>{includedLabels[index]}</span>
                </div>
              ))}
            </div>
          </section>
          </div>
        </div>
      </div>
    </div>
  );
};

const parseWarrantyYears = (years) => {
  if (!years) return null;
  const match = String(years).match(/^(\d+)\s*(.+)$/);
  if (!match) return { value: years, unit: '' };
  return { value: match[1], unit: match[2] };
};

const VmkWarrantyPage = ({ pageNumber, t }) => {
  const warrantyItems = t('quote.template.warrantyItems', { returnObjects: true }) || [];
  const certRows = [
    { label: t('quote.template.certSkg') },
    { label: t('quote.template.certPkv') },
  ];
  const rows = [...warrantyItems, ...certRows];

  return (
    <div className="quote-pdf-page vmk-warranty-page mb-4" data-pdf-page>
      <div className="vmk-proposal-shell vmk-warranty-shell">
        <VmkBrandedPageHeader pageNumber={pageNumber} className="vmk-warranty-header" />

        <div className="vmk-warranty-main">
          <h2 className="vmk-warranty-title">{t('quote.template.warrantyTitle')}</h2>
          <p className="vmk-warranty-subtitle">{t('quote.template.warrantySubtitle')}</p>

          <ul className="vmk-warranty-list">
            {rows.map((row, index) => {
              const Icon = VMK_WARRANTY_ICONS[index];
              const years = parseWarrantyYears(row.years);

              return (
                <li key={row.label} className="vmk-warranty-row">
                  <div className="vmk-warranty-icon-wrap" aria-hidden>
                    {Icon ? <Icon /> : null}
                  </div>
                  <span className="vmk-warranty-label">{row.label}</span>
                  {years ? (
                    <div className="vmk-warranty-years">
                      <span className="vmk-warranty-years-value">{years.value}</span>
                      <span className="vmk-warranty-years-unit">{years.unit}</span>
                    </div>
                  ) : (
                    <span className="vmk-warranty-years vmk-warranty-years--empty" aria-hidden />
                  )}
                </li>
              );
            })}
          </ul>

          <p className="vmk-warranty-disclaimer">{t('quote.template.warrantyDisclaimer')}</p>
        </div>

        <VmkTemplateFooter className="vmk-proposal-footer" />
      </div>
    </div>
  );
};

const VmkProcessPage = ({ pageNumber, t }) => {
  const steps = t('quote.template.processSteps', { returnObjects: true }) || [];

  return (
    <div className="quote-pdf-page vmk-process-page mb-4" data-pdf-page>
      <div className="vmk-proposal-shell vmk-process-shell">
        <VmkBrandedPageHeader pageNumber={pageNumber} className="vmk-process-header" />

        <div className="vmk-process-main">
          <h2 className="vmk-process-title">{t('quote.template.processTitle')}</h2>
          <p className="vmk-process-subtitle">{t('quote.template.processSubtitle')}</p>

          <ol className="vmk-process-timeline">
            {steps.map((step, index) => {
              const Icon = VMK_PROCESS_STEP_ICONS[index];

              return (
                <li key={step.title} className="vmk-process-step">
                  <div className="vmk-process-step-num" aria-hidden>
                    {index + 1}
                  </div>
                  <div className="vmk-process-icon-box" aria-hidden>
                    {Icon ? <Icon /> : null}
                  </div>
                  <div className="vmk-process-step-body">
                    <h3 className="vmk-process-step-title">{step.title}</h3>
                    <p className="vmk-process-step-text">{step.text}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <VmkTemplateFooter className="vmk-proposal-footer" />
      </div>
    </div>
  );
};

const VmkSustainabilityPage = ({ pageNumber, t }) => {
  const introLines = t('quote.template.sustainabilityIntro', { returnObjects: true }) || [];
  const cards = [
    { title: t('quote.template.susEnergyTitle'), text: t('quote.template.susEnergyText') },
    { title: t('quote.template.susMaterialsTitle'), text: t('quote.template.susMaterialsText') },
    { title: t('quote.template.susValueTitle'), text: t('quote.template.susValueText') },
  ];

  return (
    <div className="quote-pdf-page vmk-sustainability-page mb-4" data-pdf-page>
      <div className="vmk-proposal-shell vmk-sustainability-shell">
        <VmkBrandedPageHeader pageNumber={pageNumber} className="vmk-sustainability-header" />

        <div className="vmk-sustainability-main">
          <h2 className="vmk-sustainability-title">{t('quote.template.sustainabilityTitle')}</h2>

          <div className="vmk-sustainability-intro">
            {introLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>

          <div className="vmk-sustainability-cards">
            {cards.map(({ title, text }, index) => {
              const Icon = VMK_SUSTAINABILITY_ICONS[index];

              return (
                <article key={title} className="vmk-sustainability-card">
                  <div className="vmk-sustainability-card-icon-wrap" aria-hidden>
                    {Icon ? <Icon /> : null}
                  </div>
                  <h3 className="vmk-sustainability-card-title">{title}</h3>
                  <p className="vmk-sustainability-card-text">{text}</p>
                </article>
              );
            })}
          </div>

          <div className="vmk-sustainability-photo-wrap">
            <img
              src={publicUrl('/images/vmk-offerte-cover-front.png')}
              alt=""
              className="vmk-sustainability-photo"
            />
          </div>

          <section className="vmk-sustainability-savings">
            <h3 className="vmk-sustainability-savings-title">{t('quote.template.savingsTitle')}</h3>
            <p className="vmk-sustainability-savings-text">{t('quote.template.savingsText')}</p>
            <p className="vmk-sustainability-savings-note">{t('quote.template.savingsDisclaimer')}</p>
          </section>
        </div>

        <VmkTemplateFooter className="vmk-proposal-footer" />
      </div>
    </div>
  );
};

const splitCoverTagline = (tagline) => {
  const marker = ' EN ';
  const index = String(tagline || '').indexOf(marker);
  if (index < 0) {
    return { line1: tagline || '', line2: '' };
  }
  return {
    line1: tagline.slice(0, index),
    line2: tagline.slice(index + 1),
  };
};

export const VmkAgreementPage = ({ pageNumber, clientDetails, t, signedAgreement = null }) => {
  const recipient = signedAgreement?.name || clientDetails?.name?.trim() || '';
  const signedDate = signedAgreement?.date || '';
  const signatureDataUrl = signedAgreement?.signature || '';

  const { line1: taglineLine1, line2: taglineLine2 } = splitCoverTagline(t('quote.template.coverTagline'));

  return (
    <div className="quote-pdf-page vmk-agreement-page mb-4" data-pdf-page>
      <div className="vmk-proposal-shell vmk-agreement-shell">
        <VmkBrandedPageHeader pageNumber={pageNumber} className="vmk-agreement-header" />

        <div className="vmk-agreement-main">
          <h2 className="vmk-agreement-title">{t('quote.template.agreementTitle')}</h2>
          <p className="vmk-agreement-intro">{t('quote.template.agreementIntro')}</p>

          <div className="vmk-agreement-fields">
            <div className="vmk-agreement-field">
              <label className="vmk-agreement-label">{t('quote.template.agreementName')}</label>
              <div className="vmk-agreement-field-line">{recipient}</div>
            </div>
            <div className="vmk-agreement-field">
              <label className="vmk-agreement-label">{t('quote.template.agreementDate')}</label>
              <div className="vmk-agreement-field-line">{signedDate}</div>
            </div>
            <div className="vmk-agreement-field vmk-agreement-field--signature">
              <label className="vmk-agreement-label">{t('quote.template.agreementSignature')}</label>
              <div className="vmk-agreement-signature-box" aria-hidden>
                {signatureDataUrl ? (
                  <img src={signatureDataUrl} alt="" className="vmk-agreement-signature-image" />
                ) : null}
              </div>
            </div>
          </div>

          <div className="vmk-agreement-brand-row">
            <div className="vmk-agreement-brand">
              <img
                src={publicUrl('/images/vmk-kozijnen-logo.png')}
                alt="VMK Kozijnen"
                className="vmk-proposal-logo vmk-agreement-brand-logo"
              />
              <p className="vmk-agreement-tagline">
                {taglineLine1 ? <span>{taglineLine1}</span> : null}
                {taglineLine2 ? <span>{taglineLine2}</span> : null}
              </p>
            </div>
            <div className="vmk-agreement-qr-block">
              <VmkAgreementQrCode />
              <p className="vmk-agreement-qr-hint">{t('quote.template.agreementQrHint')}</p>
            </div>
          </div>
        </div>

        <VmkTemplateFooter className="vmk-proposal-footer" />
      </div>
    </div>
  );
};

export const VmkQuoteTemplateFront = ({ pageNumbers, quoteNumber, clientDetails, advisorName, savedQuotes = [], t }) => (
    <>
      <VmkCoverPage
        pageNumber={pageNumbers.cover}
        quoteNumber={quoteNumber}
        clientDetails={clientDetails}
        advisorName={advisorName}
        t={t}
      />

      <VmkProposalPage
        pageNumber={pageNumbers.proposal}
        clientDetails={clientDetails}
        t={t}
      />

      <VmkSpecsPage pageNumber={pageNumbers.specs} savedQuotes={savedQuotes} t={t} />
    </>
);

export const VmkQuoteTemplateBack = ({
  pageNumbers,
  quoteNumber,
  clientDetails,
  totalPrice,
  totalAreaSqm,
  formatPrice,
  getQuoteLineTotal,
  savedQuotes = [],
  signedAgreement = null,
  sectionSummaries = null,
  multiSectionMode = false,
  t,
}) => {
  return (
    <>
      <VmkInvestmentPage
        pageNumber={pageNumbers.investment}
        quoteNumber={quoteNumber}
        clientDetails={clientDetails}
        totalPrice={totalPrice}
        totalAreaSqm={totalAreaSqm}
        formatPrice={formatPrice}
        getQuoteLineTotal={getQuoteLineTotal}
        savedQuotes={savedQuotes}
        sectionSummaries={sectionSummaries}
        multiSectionMode={multiSectionMode}
        t={t}
      />

      <VmkWarrantyPage pageNumber={pageNumbers.warranty} t={t} />

      <VmkProcessPage pageNumber={pageNumbers.process} t={t} />

      <VmkSustainabilityPage pageNumber={pageNumbers.sustainability} t={t} />

      <VmkAgreementPage
        pageNumber={pageNumbers.agreement}
        clientDetails={clientDetails}
        signedAgreement={signedAgreement}
        t={t}
      />
    </>
  );
};
